/**
 * /imputacion — every authenticated user.
 *
 * Deep links: ?year=2026&month=9 opens that month (without them the server
 * picks one), ?tab=equipo opens the admins' "Equipo" tab. Non-admins get no
 * tabs at all: only their own month.
 */
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { ImputacionPanel } from './components'
import { TeamImputacionTab } from './team'

const Page = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.xl};

  @media (max-width: 639px) {
    padding: 0 ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  }
`

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.6rem 1.25rem;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.secondary)};
  color: ${({ $active }) => ($active ? 'white' : 'inherit')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
`

/** A positive integer from the query string, or undefined. */
const intParam = (v: string | null): number | undefined => {
    if (!v || !/^\d+$/.test(v)) return undefined
    const n = Number(v)
    return n > 0 ? n : undefined
}

export function ImputacionPage() {
    const [params, setParams] = useSearchParams()
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const year = intParam(params.get('year'))
    const rawMonth = intParam(params.get('month'))
    const month = rawMonth && rawMonth <= 12 ? rawMonth : undefined
    // A month without a year (or the reverse) is not a month: let the server pick.
    const hasMonth = year !== undefined && month !== undefined
    const tab = isAdmin && params.get('tab') === 'equipo' ? 'equipo' : 'mine'

    const selectTab = (next: 'mine' | 'equipo') => {
        const p = new URLSearchParams(params)
        if (next === 'equipo') p.set('tab', 'equipo')
        else p.delete('tab')
        p.delete('restore')
        setParams(p, { replace: true })
    }

    return (
        <Page>
            {isAdmin && (
                <Tabs role="tablist">
                    <Tab type="button" role="tab" aria-selected={tab === 'mine'} $active={tab === 'mine'} onClick={() => selectTab('mine')}>
                        Mi imputación
                    </Tab>
                    <Tab
                        type="button"
                        role="tab"
                        aria-selected={tab === 'equipo'}
                        $active={tab === 'equipo'}
                        onClick={() => selectTab('equipo')}
                    >
                        Equipo
                    </Tab>
                </Tabs>
            )}
            {tab === 'equipo' ? (
                <TeamImputacionTab initialYear={hasMonth ? year : undefined} initialMonth={hasMonth ? month : undefined} />
            ) : (
                <ImputacionPanel initialYear={hasMonth ? year : undefined} initialMonth={hasMonth ? month : undefined} />
            )}
        </Page>
    )
}

export default ImputacionPage
